/**
 * CompanyInteraktSettings — per-company WhatsApp (Interakt) integration.
 *
 * Interakt doesn't expose a documented public endpoint to list approved
 * WhatsApp templates. The admin must copy the template code name + body +
 * variable count from https://app.interakt.ai/templates/list and paste
 * them here. Once registered, templates become available in the bulk
 * WhatsApp dialog on the Job > Candidates view.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Phone, Save, Trash2, ExternalLink, KeyRound, History, ChevronLeft, ChevronRight } from "lucide-react";

export default function CompanyInteraktSettings() {
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [apiKey, setApiKey] = useState("");
    const [savingKey, setSavingKey] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const [s, t] = await Promise.all([
                api.get("/company/interakt/settings"),
                api.get("/company/interakt/templates"),
            ]);
            setSettings(s.data || null);
            setTemplates(t.data?.templates || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const saveKey = async () => {
        if (!apiKey.trim()) { toast.error("API key is required"); return; }
        setSavingKey(true);
        try {
            await api.put("/company/interakt/settings", { api_key: apiKey.trim() });
            toast.success("Interakt API key saved");
            setApiKey("");
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSavingKey(false); }
    };

    const removeKey = async () => {
        if (!window.confirm("Remove the saved Interakt API key? Bulk WhatsApp sending will stop until a new key is configured.")) return;
        try {
            await api.delete("/company/interakt/settings");
            toast.success("Interakt API key removed");
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeTemplate = async (t) => {
        if (!window.confirm(`Remove template "${t.name}"?`)) return;
        try {
            await api.delete(`/company/interakt/templates/${encodeURIComponent(t.name)}?language_code=${encodeURIComponent(t.language_code)}`);
            toast.success("Template removed");
            await reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="company-interakt-settings">
            <div>
                <h1 className="font-display font-bold text-2xl flex items-center gap-2">
                    <Phone className="w-6 h-6 text-emerald-600" />
                    WhatsApp (Interakt)
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Send approved WhatsApp templates to candidates in bulk from Job &gt; Candidates. Your API key is scoped to this company.
                </p>
            </div>

            {loading ? (
                <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* API KEY SECTION */}
                    <section className="border border-border bg-white" data-testid="interakt-key-section">
                        <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
                            <KeyRound className="w-4 h-4" />
                            <h2 className="font-display font-bold text-base flex-1">API key</h2>
                            {settings?.configured ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">
                                    Configured · {settings.key_last4}
                                </span>
                            ) : (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">
                                    Not configured
                                </span>
                            )}
                        </div>
                        <div className="p-4 space-y-3">
                            <p className="text-xs text-muted-foreground">
                                Grab your key from{" "}
                                <a href="https://app.interakt.ai/settings/developer-setting"
                                   target="_blank" rel="noreferrer"
                                   className="underline text-ikb inline-flex items-center gap-1">
                                    Interakt → Developer Settings
                                    <ExternalLink className="w-3 h-3" />
                                </a>.
                                We store it per-company and never return the raw value after save.
                            </p>
                            <div className="flex gap-2">
                                <Input
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder={settings?.configured ? "Paste new key to rotate" : "Paste Interakt API key"}
                                    disabled={savingKey}
                                    data-testid="interakt-key-input"
                                />
                                <Button onClick={saveKey} disabled={savingKey || !apiKey.trim()}
                                    className="rounded-none bg-ikb hover:bg-[#001f7a] text-white"
                                    data-testid="interakt-key-save">
                                    {savingKey ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                                    Save
                                </Button>
                                {settings?.configured && (
                                    <Button onClick={removeKey} variant="outline"
                                        className="rounded-none text-rose-700 border-rose-300 hover:bg-rose-50"
                                        data-testid="interakt-key-remove">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* TEMPLATES SECTION */}
                    <section className="border border-border bg-white" data-testid="interakt-templates-section">
                        <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
                            <h2 className="font-display font-bold text-base flex-1">Approved templates</h2>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                {templates.length} saved
                            </span>
                        </div>
                        <div className="p-4 border-b border-border bg-amber-50/50 text-xs text-amber-900">
                            <strong>Why do I need to add templates?</strong> Interakt doesn't expose a public API to list approved templates. Copy the template <em>code name</em> (from the URL on the Interakt dashboard — <code className="text-[11px]">app.interakt.ai/template/&lt;code-name&gt;/view</code>), the body and the number of body variables, and paste them here. We'll use that catalog to drive the Bulk WhatsApp dialog.
                        </div>
                        <TemplateForm onSaved={reload} />
                        <div className="divide-y divide-border">
                            {templates.length === 0 ? (
                                <div className="p-6 text-sm text-muted-foreground text-center">
                                    No templates registered yet. Add one above.
                                </div>
                            ) : templates.map((t) => (
                                <div key={`${t.name}::${t.language_code}`} className="p-4 flex items-start gap-3"
                                     data-testid={`interakt-template-row-${t.name}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-sm font-semibold">{t.name}</span>
                                            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-secondary border border-border">
                                                {t.language_code}
                                            </span>
                                            <span className="text-[10px] font-mono text-muted-foreground">
                                                {t.body_variables_count || 0} var{t.body_variables_count === 1 ? "" : "s"}
                                            </span>
                                        </div>
                                        {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                                        <pre className="mt-2 text-xs whitespace-pre-wrap font-mono bg-secondary/50 border border-border p-2 max-h-40 overflow-y-auto">{t.body}</pre>
                                    </div>
                                    <Button onClick={() => removeTemplate(t)} variant="outline"
                                        className="rounded-none text-rose-700 border-rose-300 hover:bg-rose-50 shrink-0"
                                        data-testid={`interakt-template-remove-${t.name}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* DELIVERY LOG SECTION */}
                    <DeliveryLog templates={templates} />
                </>
            )}
        </div>
    );
}

/**
 * DeliveryLog — paginated WhatsApp activity table.
 * Filters: status + template_name. Shows candidate name/email joined by backend.
 */
function DeliveryLog({ templates }) {
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 25;
    const [status, setStatus] = useState("");
    const [tplName, setTplName] = useState("");
    const [data, setData] = useState({ items: [], total: 0, pages: 1, facets: {} });
    const [loading, setLoading] = useState(false);

    const load = async (p = page, s = status, t = tplName) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
            if (s) params.set("status", s);
            if (t) params.set("template_name", t);
            const { data: resp } = await api.get(`/company/interakt/messages?${params.toString()}`);
            setData(resp || { items: [], total: 0, pages: 1, facets: {} });
            setPage(p);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(1, status, tplName); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, tplName]);

    const statusBadge = (s) => {
        const map = {
            accepted: "bg-emerald-100 text-emerald-800 border-emerald-300",
            failed: "bg-rose-100 text-rose-800 border-rose-300",
            no_phone: "bg-amber-100 text-amber-800 border-amber-300",
            network_error: "bg-rose-50 text-rose-700 border-rose-200",
        };
        const cls = map[s] || "bg-secondary text-foreground border-border";
        return <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{s || "unknown"}</span>;
    };

    const total = data.total || 0;
    const pages = data.pages || 1;
    const facets = data.facets || {};

    return (
        <section className="border border-border bg-white" data-testid="interakt-delivery-log">
            <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2 flex-wrap">
                <History className="w-4 h-4" />
                <h2 className="font-display font-bold text-base flex-1">Delivery log</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
                      data-testid="interakt-delivery-total">
                    {total} total
                </span>
            </div>

            <div className="p-3 border-b border-border bg-white flex items-center gap-2 flex-wrap">
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="h-8 px-2 border border-border bg-white text-xs rounded-none"
                    data-testid="interakt-delivery-filter-status"
                >
                    <option value="">All statuses{facets && Object.keys(facets).length ? ` (${Object.values(facets).reduce((a, b) => a + b, 0)})` : ""}</option>
                    {Object.entries(facets).map(([k, v]) => (
                        <option key={k} value={k}>{k} ({v})</option>
                    ))}
                </select>
                <select
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    className="h-8 px-2 border border-border bg-white text-xs rounded-none"
                    data-testid="interakt-delivery-filter-template"
                >
                    <option value="">All templates</option>
                    {templates.map((t) => (
                        <option key={`${t.name}::${t.language_code}`} value={t.name}>{t.name}</option>
                    ))}
                </select>
                {(status || tplName) && (
                    <button type="button" onClick={() => { setStatus(""); setTplName(""); }}
                        className="h-8 px-2 text-[11px] font-mono uppercase tracking-widest border border-border hover:bg-secondary"
                        data-testid="interakt-delivery-clear-filters">
                        Clear
                    </button>
                )}
                {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>

            {data.items.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center" data-testid="interakt-delivery-empty">
                    {total === 0 && !status && !tplName
                        ? "No WhatsApp activity yet. Send a bulk message from the Job > Candidates page to populate this log."
                        : "No rows match the current filters."}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="interakt-delivery-table">
                        <thead className="bg-secondary/50 border-b border-border">
                            <tr className="text-left font-mono uppercase tracking-widest text-[10px] text-foreground/70">
                                <th className="px-3 py-2">When</th>
                                <th className="px-3 py-2">Candidate</th>
                                <th className="px-3 py-2">Template</th>
                                <th className="px-3 py-2">Phone</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/20"
                                    data-testid={`interakt-delivery-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
                                        {new Date(r.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-3 py-2 min-w-[160px]">
                                        <div className="font-semibold">{r.candidate_name || "—"}</div>
                                        {r.candidate_email && <div className="text-[11px] text-muted-foreground truncate">{r.candidate_email}</div>}
                                    </td>
                                    <td className="px-3 py-2 font-mono">
                                        {r.template_name}
                                        <span className="ml-1 text-[10px] text-muted-foreground">· {r.language_code}</span>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
                                        {r.phone_cc ? `${r.phone_cc} ${r.phone_national || ""}` : "—"}
                                    </td>
                                    <td className="px-3 py-2">{statusBadge(r.status)}</td>
                                    <td className="px-3 py-2 text-[11px] text-rose-700 max-w-[260px] truncate" title={r.error || ""}>
                                        {r.http_status ? `${r.http_status} · ` : ""}{r.error || (r.status === "accepted" ? "—" : "")}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pages > 1 && (
                <div className="px-3 py-2 border-t border-border flex items-center justify-between">
                    <span className="text-[11px] font-mono text-muted-foreground"
                          data-testid="interakt-delivery-page-info">
                        Page {page} of {pages}
                    </span>
                    <div className="flex gap-1">
                        <button type="button" disabled={page <= 1 || loading}
                            onClick={() => load(page - 1, status, tplName)}
                            className="h-7 w-7 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30"
                            data-testid="interakt-delivery-prev">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button type="button" disabled={page >= pages || loading}
                            onClick={() => load(page + 1, status, tplName)}
                            className="h-7 w-7 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30"
                            data-testid="interakt-delivery-next">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}

function TemplateForm({ onSaved }) {
    const [name, setName] = useState("");
    const [language, setLanguage] = useState("en");
    const [body, setBody] = useState("");
    const [varsCount, setVarsCount] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !body.trim()) { toast.error("Name and body are required"); return; }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                language_code: language.trim() || "en",
                body: body.trim(),
                description: description.trim() || null,
            };
            if (varsCount !== "") payload.body_variables_count = Number(varsCount);
            await api.post("/company/interakt/templates", payload);
            toast.success("Template saved");
            setName(""); setBody(""); setVarsCount(""); setDescription(""); setLanguage("en");
            onSaved?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="p-4 border-b border-border grid grid-cols-1 md:grid-cols-2 gap-3"
              data-testid="interakt-template-form">
            <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest">Template code name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="employee_welcome_v2"
                       disabled={saving} data-testid="interakt-template-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest">Language</Label>
                    <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en"
                           disabled={saving} data-testid="interakt-template-lang" />
                </div>
                <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest">Vars (0 = auto)</Label>
                    <Input value={varsCount} onChange={(e) => setVarsCount(e.target.value.replace(/[^\d]/g, ""))} placeholder="auto"
                           disabled={saving} data-testid="interakt-template-vars" />
                </div>
            </div>
            <div className="md:col-span-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest">Body (as approved on Interakt)</Label>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                          rows={4} placeholder={"Hi {{1}}, thanks for applying to {{2}}. We'll be in touch soon."}
                          disabled={saving} data-testid="interakt-template-body" />
                <p className="text-[11px] text-muted-foreground mt-1">
                    Use <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code> … exactly like Meta/Interakt. We auto-detect the count.
                </p>
            </div>
            <div className="md:col-span-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest">Internal description (optional)</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)}
                       placeholder="e.g. First-touch welcome used after Applied → Screening"
                       disabled={saving} data-testid="interakt-template-desc" />
            </div>
            <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={saving || !name.trim() || !body.trim()}
                    className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="interakt-template-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                    Save template
                </Button>
            </div>
        </form>
    );
}
